import { PostedState } from "../../components/posting";
import { ClaimStatus } from "../../status";
import type {
  FilterList,
  FilterOperatorConfig,
} from "../../utils/backendFilters";
import { camelToSnakeCase } from "../../utils/utils";

export type ClaimsFilters = {
  review_status?: FilterList;
  wieldy_id?: FilterList;
  [key: string]: FilterList | FilterOperatorConfig | undefined;
};

const isDeltaDental = (payer: string) =>
  typeof payer === "string" && payer.toLowerCase().includes("delta dental");

export const normalizePayer = (payer: string) =>
  isDeltaDental(payer) ? "Delta Dental" : payer;

export interface ClaimFiltersConfig {
  includeNonEFTs?: boolean;
  includeClaimReview?: boolean;
  includeClaimDenials?: boolean;
  additionalFilters?: Record<string, FilterList | FilterOperatorConfig>;
}

export function generateClaimFilters(
  config: ClaimFiltersConfig,
): Record<string, FilterList | FilterOperatorConfig> {
  const {
    includeNonEFTs = false,
    includeClaimReview = false,
    includeClaimDenials = false,
    additionalFilters = {},
  } = config;

  const filters: Record<string, FilterList | FilterOperatorConfig> = {
    claim_type: [null, "CLAIM"],
    claim_status: [
      null,
      ClaimStatus.APPROVED,
      ClaimStatus.DENIED,
      ClaimStatus.PARTIALLY_DENIED,
    ],
  };

  // Add review filters if needed
  if (includeClaimReview) {
    filters.posted_state = [
      PostedState[PostedState.UNPOSTED],
      PostedState[PostedState.FAILURE],
      PostedState[PostedState.RETRY],
      PostedState[PostedState.PENDING],
      PostedState[PostedState.POSTING],
      PostedState[PostedState.REQUEUED],
    ].filter(Boolean) as FilterList;
  }

  if (includeClaimDenials) {
    filters.claim_status = [
      ClaimStatus[ClaimStatus.DENIED],
      ClaimStatus[ClaimStatus.PARTIALLY_DENIED],
    ].filter(Boolean) as FilterList;
  }

  /* this currently has no effect on the backend and
  we explicitly filter for EFTs in the backend
  TODO: Enable this once we have a backend filter for EFTs */
  if (!includeNonEFTs) {
    // filters.payment_type = [null, "EFT"];
  }

  return { ...filters, ...additionalFilters };
}

/**
 * Returns filter configuration for claims needing review
 * (filtering for both Claims list and Postie views)
 */
export function getReviewClaimsFilters(
  additionalFilters: Record<string, FilterList | FilterOperatorConfig> = {},
): Record<string, FilterList | FilterOperatorConfig> {
  return generateClaimFilters({
    includeNonEFTs: false,
    includeClaimReview: true,
    additionalFilters,
  });
}

/**
 * Returns filter configuration for denied claims
 */
export function getDeniedClaimsFilters(
  additionalFilters: Record<string, FilterList | FilterOperatorConfig> = {},
): Record<string, FilterList | FilterOperatorConfig> {
  return generateClaimFilters({
    includeNonEFTs: false,
    includeClaimDenials: true,
    additionalFilters,
  });
}

/**
 * Returns filter configuration for all claims
 */
export function getClaimFilters(
  additionalFilters: Record<string, FilterList | FilterOperatorConfig> = {},
): Record<string, FilterList | FilterOperatorConfig> {
  return generateClaimFilters({
    includeNonEFTs: false,
    includeClaimReview: false,
    includeClaimDenials: false,
    additionalFilters,
  });
}

export const CLAIMS_SORT_FIELD_MAP: Record<string, string> = {
  availableSince: "first_retrieved_date",
  claimPayerPays: "claim_payer_pays",
  claimId: "claim_id",
  daysSinceNotice: "days_since_notice",
  daysSinceService: "days_since_service",
  urgency: "urgency",
  claimStatus: "claim_status",
  payer: "payer",
  source: "source",
  wieldyPatientName: "wieldy_patient_name",
  postedDateTime: "posted_datetime",
};

export const CLAIM_FILTER_FIELD_MAP: Record<string, string> = {
  available_since: "first_retrieved_date",
  posted_date_time: "posted_datetime",
};

export function mapClaimFilterField(frontendField: string): string {
  const snakeCase = camelToSnakeCase(frontendField);
  return CLAIM_FILTER_FIELD_MAP[snakeCase] ?? snakeCase;
}

export function mapClaimSortField(frontendField: string): string {
  return (
    CLAIMS_SORT_FIELD_MAP[frontendField] || camelToSnakeCase(frontendField)
  );
}
